import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import type { Plan, SyncPlansResult } from '@/types';

/**
 * Wave 2B — Super admin plans editor.
 *
 * Lists every row in `public.plans`. Editable columns are exposed as inline
 * inputs / switches with a per-row "Guardar" button that calls the
 * `superAdminAction` edge function with `action: 'plan_updated'` and a diff.
 * Read-only columns (`slug`, `stripe_*`, `kind`, `billing_cycle`) render as
 * plain text — those are written by Wave 2C's Stripe sync.
 *
 * The "Sincronizar a Stripe" button is a disabled placeholder until Wave 2C.
 *
 * Type note: until Sergio re-runs `supabase gen types typescript`, the
 * generated `Database` interface in `src/integrations/supabase/types.ts`
 * does not include `plans`, so all table access casts the client through
 * `unknown`. This mirrors the pattern used in
 * `src/pages/SuperAdmin/AuditLog.tsx`.
 */

const PLAN_KIND_LABELS: Record<Plan['kind'], string> = {
  B2B_PLANNER: 'B2B (Planner)',
  B2C_PARTY: 'B2C (Fiesta)',
  TRIAL: 'Prueba',
};

const BILLING_CYCLE_LABELS: Record<Plan['billing_cycle'], string> = {
  MONTH: 'Mensual',
  YEAR: 'Anual',
  ONE_TIME_6MO: 'Pago único (6 meses)',
  NONE: '—',
};

const mxnFormatter = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  maximumFractionDigits: 2,
});

function formatMxn(cents: number): string {
  return mxnFormatter.format(cents / 100);
}

function formatBytes(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return 'Ilimitado';
  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

/**
 * Plans listing — `is_active = true OR is_super_admin()` RLS gate already
 * gives the super admin every row; we sort by `sort_order` to match what
 * the public pricing page will show.
 */
async function fetchPlans(): Promise<Plan[]> {
  const client = supabase as unknown as {
    from: (t: string) => {
      select: (cols: string) => {
        order: (col: string, opts: { ascending: boolean }) => Promise<{
          data: Plan[] | null;
          error: unknown;
        }>;
      };
    };
  };
  const { data, error } = await client
    .from('plans')
    .select('*')
    .order('sort_order', { ascending: true });
  if (error) {
    console.error('PlansAdmin: failed to load plans', error);
    throw error;
  }
  return data ?? [];
}

interface PlanUpdateChanges {
  display_name?: string;
  price_mxn_cents?: number;
  events_per_period?: number | null;
  active_events_cap?: number | null;
  whatsapp_messages_per_period?: number | null;
  storage_bytes?: number | null;
  users_per_workspace?: number | null;
  trial_days?: number | null;
  trial_event_cap?: number | null;
  is_active?: boolean;
  show_on_pricing_page?: boolean;
  sort_order?: number;
}

async function savePlan(planId: string, changes: PlanUpdateChanges): Promise<{ audit_id: string }> {
  const { data, error } = await supabase.functions.invoke('superAdminAction', {
    body: {
      action: 'plan_updated',
      payload: { plan_id: planId, changes },
    },
  });
  if (error) throw error;
  const result = data as { ok?: boolean; audit_id?: string; error?: string } | null;
  if (!result?.ok || !result.audit_id) {
    throw new Error(result?.error ?? 'No se pudo guardar el plan.');
  }
  return { audit_id: result.audit_id };
}

/**
 * Per-row draft state. We keep everything as strings for the numeric inputs
 * so we can distinguish between "empty" (= NULL / unlimited) and "0".
 */
interface PlanDraft {
  display_name: string;
  price_mxn_cents: string;
  events_per_period: string;
  active_events_cap: string;
  whatsapp_messages_per_period: string;
  storage_bytes: string;
  users_per_workspace: string;
  trial_days: string;
  trial_event_cap: string;
  is_active: boolean;
  show_on_pricing_page: boolean;
  sort_order: string;
}

function planToDraft(plan: Plan): PlanDraft {
  const numOrEmpty = (n: number | null): string =>
    n === null || n === undefined ? '' : String(n);
  return {
    display_name: plan.display_name,
    price_mxn_cents: String(plan.price_mxn_cents),
    events_per_period: numOrEmpty(plan.events_per_period),
    active_events_cap: numOrEmpty(plan.active_events_cap),
    whatsapp_messages_per_period: numOrEmpty(plan.whatsapp_messages_per_period),
    storage_bytes: numOrEmpty(plan.storage_bytes),
    users_per_workspace: numOrEmpty(plan.users_per_workspace),
    trial_days: numOrEmpty(plan.trial_days),
    trial_event_cap: numOrEmpty(plan.trial_event_cap),
    is_active: plan.is_active,
    show_on_pricing_page: plan.show_on_pricing_page,
    sort_order: String(plan.sort_order),
  };
}

/**
 * Build a partial-update payload from a plan + its current draft.
 * Returns `null` when nothing changed (so the save button can be disabled
 * and the mutation never fires for a no-op).
 *
 * Throws if a required-numeric field is malformed — the caller surfaces a
 * toast.
 */
function diffPlan(plan: Plan, draft: PlanDraft): PlanUpdateChanges | null {
  const changes: PlanUpdateChanges = {};

  const parseRequiredInt = (raw: string, label: string): number => {
    const n = Number(raw);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
      throw new Error(`${label}: usa un entero ≥ 0.`);
    }
    return n;
  };
  const parseNullableInt = (raw: string, label: string): number | null => {
    if (raw.trim() === '') return null;
    return parseRequiredInt(raw, label);
  };

  const trimmedName = draft.display_name.trim();
  if (trimmedName.length === 0) {
    throw new Error('Nombre: no puede estar vacío.');
  }
  if (trimmedName !== plan.display_name) changes.display_name = trimmedName;

  const priceCents = parseRequiredInt(draft.price_mxn_cents, 'Precio (centavos)');
  if (priceCents !== plan.price_mxn_cents) changes.price_mxn_cents = priceCents;

  const sortOrder = parseRequiredInt(draft.sort_order, 'Orden');
  if (sortOrder !== plan.sort_order) changes.sort_order = sortOrder;

  const eventsPerPeriod = parseNullableInt(draft.events_per_period, 'Eventos por periodo');
  if (eventsPerPeriod !== plan.events_per_period) changes.events_per_period = eventsPerPeriod;

  const activeEventsCap = parseNullableInt(draft.active_events_cap, 'Eventos activos');
  if (activeEventsCap !== plan.active_events_cap) changes.active_events_cap = activeEventsCap;

  const waPerPeriod = parseNullableInt(draft.whatsapp_messages_per_period, 'Mensajes WhatsApp');
  if (waPerPeriod !== plan.whatsapp_messages_per_period)
    changes.whatsapp_messages_per_period = waPerPeriod;

  const storageBytes = parseNullableInt(draft.storage_bytes, 'Almacenamiento (bytes)');
  if (storageBytes !== plan.storage_bytes) changes.storage_bytes = storageBytes;

  const usersPerWorkspace = parseNullableInt(draft.users_per_workspace, 'Usuarios por workspace');
  if (usersPerWorkspace !== plan.users_per_workspace)
    changes.users_per_workspace = usersPerWorkspace;

  const trialDays = parseNullableInt(draft.trial_days, 'Días de prueba');
  if (trialDays !== plan.trial_days) changes.trial_days = trialDays;

  const trialEventCap = parseNullableInt(draft.trial_event_cap, 'Eventos en prueba');
  if (trialEventCap !== plan.trial_event_cap) changes.trial_event_cap = trialEventCap;

  if (draft.is_active !== plan.is_active) changes.is_active = draft.is_active;
  if (draft.show_on_pricing_page !== plan.show_on_pricing_page)
    changes.show_on_pricing_page = draft.show_on_pricing_page;

  return Object.keys(changes).length === 0 ? null : changes;
}

export const PlansAdmin: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const plansQuery = useQuery({
    queryKey: ['superadmin', 'plans'],
    queryFn: fetchPlans,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Planes</h1>
          <p className="text-sm text-gray-500 mt-1">
            Edita precios, cupos y visibilidad. Los cambios se registran en auditoría.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled
          title="Disponible cuando Stripe esté integrado (Wave 2C)"
        >
          Sincronizar a Stripe (Wave 2C)
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Catálogo de planes</CardTitle>
        </CardHeader>
        <CardContent>
          {plansQuery.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : plansQuery.error ? (
            <p className="text-sm text-red-600 py-4">
              No se pudieron cargar los planes. Revisa la consola para más detalles.
            </p>
          ) : (plansQuery.data ?? []).length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No hay planes registrados.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Slug</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Ciclo</TableHead>
                    <TableHead>Precio MXN</TableHead>
                    <TableHead>Eventos / periodo</TableHead>
                    <TableHead>Eventos activos</TableHead>
                    <TableHead>Mensajes WhatsApp</TableHead>
                    <TableHead>Almacenamiento</TableHead>
                    <TableHead>Usuarios</TableHead>
                    <TableHead>Días prueba</TableHead>
                    <TableHead>Eventos prueba</TableHead>
                    <TableHead>Orden</TableHead>
                    <TableHead>Activo</TableHead>
                    <TableHead>En precios</TableHead>
                    <TableHead>Stripe Product</TableHead>
                    <TableHead>Stripe Price</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(plansQuery.data ?? []).map((plan) => (
                    <PlanRow
                      key={plan.id}
                      plan={plan}
                      onSaved={() => {
                        queryClient.invalidateQueries({
                          queryKey: ['superadmin', 'plans'],
                        });
                      }}
                      onError={(msg) => {
                        toast({
                          title: 'No se pudo guardar',
                          description: msg,
                          variant: 'destructive',
                        });
                      }}
                      onSuccess={(label) => {
                        toast({
                          title: 'Plan actualizado',
                          description: label,
                        });
                      }}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

interface PlanRowProps {
  plan: Plan;
  onSaved: () => void;
  onSuccess: (label: string) => void;
  onError: (msg: string) => void;
}

const PlanRow: React.FC<PlanRowProps> = ({ plan, onSaved, onSuccess, onError }) => {
  const [draft, setDraft] = useState<PlanDraft>(() => planToDraft(plan));

  // Re-sync the draft when the underlying plan changes (e.g. after a save
  // refetch returns updated data). useMemo over the plan id + updated_at
  // tuple keeps this cheap.
  const planSignature = useMemo(
    () => `${plan.id}:${plan.updated_at}`,
    [plan.id, plan.updated_at],
  );
  const lastSignatureRef = React.useRef(planSignature);
  React.useEffect(() => {
    if (lastSignatureRef.current !== planSignature) {
      lastSignatureRef.current = planSignature;
      setDraft(planToDraft(plan));
    }
  }, [plan, planSignature]);

  const mutation = useMutation({
    mutationFn: (changes: PlanUpdateChanges) => savePlan(plan.id, changes),
    onSuccess: () => {
      onSaved();
      onSuccess(plan.display_name);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Error desconocido.';
      onError(msg);
    },
  });

  const setField = <K extends keyof PlanDraft>(key: K, value: PlanDraft[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    let diff: PlanUpdateChanges | null;
    try {
      diff = diffPlan(plan, draft);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Datos inválidos.';
      onError(msg);
      return;
    }
    if (diff === null) {
      onError('No hay cambios para guardar.');
      return;
    }
    mutation.mutate(diff);
  };

  const isSaving = mutation.isPending;

  return (
    <TableRow>
      <TableCell className="font-mono text-xs text-gray-600 whitespace-nowrap">
        {plan.slug}
      </TableCell>
      <TableCell>
        <Input
          value={draft.display_name}
          onChange={(e) => setField('display_name', e.target.value)}
          className="min-w-[10rem]"
          aria-label={`Nombre del plan ${plan.slug}`}
        />
      </TableCell>
      <TableCell className="text-xs text-gray-700 whitespace-nowrap">
        <Badge variant="secondary">{PLAN_KIND_LABELS[plan.kind]}</Badge>
      </TableCell>
      <TableCell className="text-xs text-gray-700 whitespace-nowrap">
        {BILLING_CYCLE_LABELS[plan.billing_cycle]}
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          <Input
            type="number"
            min={0}
            step={1}
            value={draft.price_mxn_cents}
            onChange={(e) => setField('price_mxn_cents', e.target.value)}
            className="w-28"
            aria-label={`Precio en centavos del plan ${plan.slug}`}
          />
          <span className="text-[11px] text-gray-500">
            {formatMxn(Number(draft.price_mxn_cents) || 0)}
          </span>
        </div>
      </TableCell>
      <TableCell>
        <NullableNumberInput
          value={draft.events_per_period}
          onChange={(v) => setField('events_per_period', v)}
          aria-label={`Eventos por periodo del plan ${plan.slug}`}
        />
      </TableCell>
      <TableCell>
        <NullableNumberInput
          value={draft.active_events_cap}
          onChange={(v) => setField('active_events_cap', v)}
          aria-label={`Cupo de eventos activos del plan ${plan.slug}`}
        />
      </TableCell>
      <TableCell>
        <NullableNumberInput
          value={draft.whatsapp_messages_per_period}
          onChange={(v) => setField('whatsapp_messages_per_period', v)}
          aria-label={`Mensajes WhatsApp del plan ${plan.slug}`}
        />
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          <NullableNumberInput
            value={draft.storage_bytes}
            onChange={(v) => setField('storage_bytes', v)}
            aria-label={`Almacenamiento (bytes) del plan ${plan.slug}`}
            width="w-32"
          />
          <span className="text-[11px] text-gray-500">
            {draft.storage_bytes.trim() === ''
              ? 'Ilimitado'
              : formatBytes(Number(draft.storage_bytes))}
          </span>
        </div>
      </TableCell>
      <TableCell>
        <NullableNumberInput
          value={draft.users_per_workspace}
          onChange={(v) => setField('users_per_workspace', v)}
          aria-label={`Usuarios por workspace del plan ${plan.slug}`}
        />
      </TableCell>
      <TableCell>
        <NullableNumberInput
          value={draft.trial_days}
          onChange={(v) => setField('trial_days', v)}
          aria-label={`Días de prueba del plan ${plan.slug}`}
        />
      </TableCell>
      <TableCell>
        <NullableNumberInput
          value={draft.trial_event_cap}
          onChange={(v) => setField('trial_event_cap', v)}
          aria-label={`Eventos en prueba del plan ${plan.slug}`}
        />
      </TableCell>
      <TableCell>
        <Input
          type="number"
          min={0}
          step={1}
          value={draft.sort_order}
          onChange={(e) => setField('sort_order', e.target.value)}
          className="w-20"
          aria-label={`Orden del plan ${plan.slug}`}
        />
      </TableCell>
      <TableCell>
        <Switch
          checked={draft.is_active}
          onCheckedChange={(v) => setField('is_active', v)}
          aria-label={`Activar plan ${plan.slug}`}
        />
      </TableCell>
      <TableCell>
        <Switch
          checked={draft.show_on_pricing_page}
          onCheckedChange={(v) => setField('show_on_pricing_page', v)}
          aria-label={`Mostrar plan ${plan.slug} en página de precios`}
        />
      </TableCell>
      <TableCell className="font-mono text-[11px] text-gray-500">
        {plan.stripe_product_id ?? '—'}
      </TableCell>
      <TableCell className="font-mono text-[11px] text-gray-500">
        {plan.stripe_price_id ?? '—'}
      </TableCell>
      <TableCell>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={isSaving}
          aria-label={`Guardar cambios del plan ${plan.slug}`}
        >
          {isSaving ? (
            <>
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              Guardando…
            </>
          ) : (
            'Guardar'
          )}
        </Button>
      </TableCell>
    </TableRow>
  );
};

interface NullableNumberInputProps {
  value: string;
  onChange: (v: string) => void;
  'aria-label': string;
  width?: string;
}

/**
 * Input where an empty value means "Ilimitado" (= NULL in the DB). The
 * input is text-mode (instead of number) so we can preserve the empty
 * string reliably; the parent component validates on save.
 */
const NullableNumberInput: React.FC<NullableNumberInputProps> = ({
  value,
  onChange,
  width,
  ...rest
}) => (
  <Input
    type="text"
    inputMode="numeric"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder="Ilimitado"
    className={width ?? 'w-24'}
    {...rest}
  />
);

export default PlansAdmin;
